/**
 * @file DOM heads-up display (GDD §13, Fase 3/4).
 *
 * The HUD is deliberately DOM rather than in-scene: text stays crisp, costs no
 * draw calls, and the UI layer cannot accidentally reach into gameplay. It is
 * fed entirely by events plus a per-frame read of pointer position and FPS.
 */

import * as THREE from 'three';
import { Events } from '../core/EventBus.js';
import { GameStates } from '../core/states/StateMachine.js';
import {
  PlayerControlled,
  Weapon,
  Transform,
  StatusEffects,
  TimedStatus
} from '../components/index.js';
import { describeWeapon } from '../utils/WeaponDebug.js';
import { ENEMIES } from '../config/Enemies.js';
import { getPlayerTuning } from '../config/PlayerTuning.js';
import { getRandomLevelName } from '../config/LevelNames.js';
import { DEFAULT_WEAPON, WEAPONS } from '../config/Weapons.js';
import { weaponSprite } from '../config/WeaponArt.js';
import { Action, keyLabelsFor } from '../input/InputManager.js';
import { InputMode } from '../input/deviceProfile.js';
import { TOUCH_BUTTONS } from './TouchControls.js';
import { emptyWeaponAction } from '../combat/ammoPolicy.js';

/**
 * Smallest effective HUD scale.
 *
 * The floor used to be 1, which meant the HUD could never shrink below the
 * ~1040x560 canvas it is authored for and simply overflowed a phone instead.
 * It cannot be dropped altogether either: `CONFIG.hud.scale` is 3, so a 390px
 * portrait screen computes 0.375 and the 11px body copy stops being legible.
 * Below this the oversized panels are narrowed by CSS instead — see
 * `.pulse-bar` / `.objective-panel` in the max-width media query.
 */
const MIN_UI_SCALE = 0.55;

/** Seconds the exit animation runs before the toast's DOM node is removed. */
const NOTIFICATION_EXIT = 0.2;
/** Opacity the damage flash tint peaks at; the colour itself is configurable. */
const FLASH_ALPHA = 0.55;

/**
 * @param {number} hex Packed `0xRRGGBB` colour, the form the config tables use.
 * @param {number} alpha 0..1.
 * @returns {string} A CSS `rgba()` string.
 */
function rgbaFromHex(hex, alpha) {
  const packed = Math.max(0, Math.trunc(hex)) & 0xffffff;
  return `rgba(${(packed >> 16) & 0xff}, ${(packed >> 8) & 0xff}, ${packed & 0xff}, ${alpha})`;
}
/** Seconds a kill toast suppresses further kill toasts, so streaks don't spam. */
const KILL_COOLDOWN = 0.5;
/** Editable bounds for the HUD scale control hosted by the debug window. */
export const HUD_SCALE_CONTROLS = Object.freeze([
  {
    id: 'overall',
    label: 'Overall',
    hint: 'Shared multiplier for the gameplay HUD.',
    min: 1,
    max: 4,
    step: 0.25
  },
  {
    id: 'crosshair',
    label: 'Crosshair',
    hint: 'Aim reticle and menu pointer dot.',
    min: 0.5,
    max: 2,
    step: 0.05
  },
  {
    id: 'timer',
    label: 'Countdown',
    hint: 'Top-center kill timer bar.',
    min: 0.5,
    max: 2,
    step: 0.05
  },
  {
    id: 'objective',
    label: 'Objective',
    hint: 'Exit compass and distance card.',
    min: 0.5,
    max: 2,
    step: 0.05
  },
  {
    id: 'weapon',
    label: 'Weapon',
    hint: 'Equipped weapon card and ammo readout.',
    min: 0.5,
    max: 2,
    step: 0.05
  },
  {
    id: 'status',
    label: 'Status',
    hint: 'Shield, buffs and weapon debug text.',
    min: 0.5,
    max: 2,
    step: 0.05
  },
  {
    id: 'notifications',
    label: 'Notifications',
    hint: 'Score panel and corner toasts.',
    min: 0.5,
    max: 2,
    step: 0.05
  },
  {
    id: 'prompts',
    label: 'Prompts',
    hint: 'Interaction and empty-ammo prompts.',
    min: 0.5,
    max: 2,
    step: 0.05
  },
  {
    id: 'banner',
    label: 'Banner',
    hint: 'Level and wave callouts.',
    min: 0.5,
    max: 2,
    step: 0.05
  }
]);

const HUD_SCALE_META = new Map(HUD_SCALE_CONTROLS.map((row) => [row.id, row]));
const HUD_COMPONENT_SCALE_IDS = HUD_SCALE_CONTROLS
  .filter((row) => row.id !== 'overall')
  .map((row) => row.id);

/**
 * @param {number|string} value Candidate scale.
 * @param {string} [id] Scale id; defaults to the overall HUD scale.
 * @param {number} [fallback] Value to keep when the candidate is invalid.
 * @returns {number} Scale clamped to the supported HUD range.
 */
export function coerceHudScale(value, id = 'overall', fallback = 1) {
  const meta = HUD_SCALE_META.get(id) ?? HUD_SCALE_META.get('overall');
  const numeric = Number(value);
  const base = Number.isFinite(numeric) ? numeric : fallback;
  return Math.min(meta.max, Math.max(meta.min, Math.round(base * 100) / 100));
}

/**
 * @param {Record<string, number>} source Configured component scales.
 * @returns {Record<string, number>} Normalized component scale map.
 */
function buildComponentScales(source = {}) {
  const result = {};
  for (const id of HUD_COMPONENT_SCALE_IDS) {
    result[id] = coerceHudScale(source[id], id, 1);
  }
  return result;
}

/**
 * Overlay copy per state. A state missing from the table shows no overlay.
 * `layout: 'menu'` gets the nav list + input/audio panel
 * (`menu_1.png`/`menu_2.png`); `layout: 'simple'` keeps the plain
 * title/subtitle/hint card LOADING and GAME_OVER always used.
 */
const OVERLAYS = {
  [GameStates.MENU]: {
    layout: 'menu',
    title: 'FRENZY',
    subtitle: 'Find the exit portal\nKill enemies before the timer runs out',
    nav: [{ action: 'play', label: 'Play', icon: 'play', primary: true }]
  },
  [GameStates.LOADING]: {
    layout: 'simple',
    title: '',
    subtitle: 'Generating dungeon…',
    hint: 'Cannot play until the level is ready'
  },
  [GameStates.PAUSED]: {
    layout: 'menu',
    title: 'PAUSED',
    subtitle: 'Find the exit portal\nKill enemies before the timer runs out',
    nav: [
      { action: 'resume', label: 'Resume', icon: 'play', primary: true },
      { action: 'mainMenu', label: 'Main Menu', icon: 'house' }
    ]
  },
  [GameStates.GAME_OVER]: {
    layout: 'simple',
    title: 'YOU DIED!',
    subtitle: '',
    hint: 'Click to retry · Esc for menu'
  }
};

/**
 * Inline SVG glyphs for the nav list. Hand-drawn rather than an icon font or
 * library — two simple shapes do not earn a dependency — and coloured via
 * `currentColor` so the existing hover/active CSS just works.
 */
const NAV_ICONS = {
  play: '<svg viewBox="0 0 16 16"><path d="M3 2 13 8 3 14Z" fill="currentColor"/></svg>',
  house: '<svg viewBox="0 0 16 16"><path d="M8 1 15 7 13 7 13 14 3 14 3 7 1 7Z" fill="currentColor"/></svg>'
};

/** Segments in the "kill to keep your heart beating" bar (cosmetic only; see {@link HUD#_setKillTimer}). */
const PULSE_SEGMENTS = 10;
/** Bar colour at zero seconds left (urgent) and at a full countdown (safe). */
const PULSE_COLOR_LOW = [255, 84, 112];
const PULSE_COLOR_HIGH = [74, 143, 255];

/**
 * Lerps {@link PULSE_COLOR_LOW} to {@link PULSE_COLOR_HIGH} across the bar so the
 * skull end always reads hot and the far end always reads cool, regardless of
 * how many segments are lit.
 * @param {number} index Segment index, 0-based.
 * @param {number} count Total segment count.
 * @returns {string} `rgb(...)` CSS colour.
 */
function pulseSegmentColor(index, count) {
  const t = count > 1 ? index / (count - 1) : 0;
  const r = Math.round(PULSE_COLOR_LOW[0] + (PULSE_COLOR_HIGH[0] - PULSE_COLOR_LOW[0]) * t);
  const g = Math.round(PULSE_COLOR_LOW[1] + (PULSE_COLOR_HIGH[1] - PULSE_COLOR_LOW[1]) * t);
  const b = Math.round(PULSE_COLOR_LOW[2] + (PULSE_COLOR_HIGH[2] - PULSE_COLOR_LOW[2]) * t);
  return `rgb(${r}, ${g}, ${b})`;
}

/** Builds the static row of coloured segment nodes for the pulse bar. */
function pulseSegmentsHtml() {
  let html = '';
  for (let i = 0; i < PULSE_SEGMENTS; i++) {
    html += `<i class="pulse-bar__segment" style="background:${pulseSegmentColor(i, PULSE_SEGMENTS)}"></i>`;
  }
  return html;
}

/**
 * @param {unknown} effects Candidate status bag.
 * @param {string} status Status id.
 * @returns {number} Seconds left, or 0 when inactive.
 */
function statusRemaining(effects, status) {
  return effects instanceof StatusEffects ? effects.remainingOf(status) : 0;
}

/** Compass arrow: points up (north) at rotate(0deg); {@link HUD#_renderObjective} rotates it. */
const COMPASS_ARROW_ICON =
  '<svg viewBox="0 0 24 24"><path d="M12 2 19 21 12 17 5 21Z"/></svg>';
// Rounded cranium, angled almond eye sockets, triangular nose and four
// rounded teeth — closer to the reference art than the old blob. Eyes and
// nose are inner subpaths cut out via fill-rule="evenodd" (the CSS sets
// `fill: currentColor` but no rule, so the attribute has to carry it).
const SKULL_ICON =
  '<svg viewBox="0 0 24 24"><path fill-rule="evenodd" d="' +
  'M12 2C7 2 3.3 5.9 3.3 10.7C3.3 12.9 4.2 14.8 5.9 16C6.6 16.5 7.2 17 7.2 17.8' +
  'C7.2 19.3 7.6 20.6 8.4 20.6C9.2 20.6 9.6 19.6 9.6 18.4C9.6 19.6 10 20.6 10.8 20.6' +
  'C11.6 20.6 12 19.6 12 18.4C12 19.6 12.4 20.6 13.2 20.6C14 20.6 14.4 19.6 14.4 18.4' +
  'C14.4 19.6 14.8 20.6 15.6 20.6C16.4 20.6 16.8 19.3 16.8 17.8C16.8 17 17.4 16.5 18.1 16' +
  'C19.8 14.8 20.7 12.9 20.7 10.7C20.7 5.9 17 2 12 2Z' +
  'M6.4 9.9C8 8.9 10 9.7 10.2 11.3C8.6 12.3 6.6 11.5 6.4 9.9Z' +
  'M17.6 9.9C16 8.9 14 9.7 13.8 11.3C15.4 12.3 17.4 11.5 17.6 9.9Z' +
  'M12 13C12.5 13.8 13 14.5 13 14.8C13 15.1 12.6 15.2 12 15.2C11.4 15.2 11 15.1 11 14.8' +
  'C11 14.5 11.5 13.8 12 13Z' +
  '"/></svg>';
const FLOOR_ICON =
  '<svg viewBox="0 0 24 24"><path d="M12 3 22 8 12 13 2 8Z"/><path d="M2 12 12 17 22 12 22 14 12 19 2 14Z" opacity="0.7"/><path d="M2 16 12 21 22 16 22 18 12 23 2 18Z" opacity="0.45"/></svg>';
const SCORE_ICON =
  '<svg viewBox="0 0 24 24"><path d="M12 2 15 9 22 10 17 15 18.5 22 12 18.5 5.5 22 7 15 2 10 9 9Z"/></svg>';
const BULLET_ICON = '<svg viewBox="0 0 8 14"><path d="M4 0 7 4 7 14 1 14 1 4Z"/></svg>';
const SHIELD_ICON =
  '<svg viewBox="0 0 24 24"><path d="M12 2 20 5.5 20 11C20 16.2 16.8 20.4 12 22 7.2 20.4 4 16.2 4 11L4 5.5Z"/></svg>';

/**
 * Player-facing input rows shared by the gameplay HUD and menu legend.
 *
 * The touch rows are derived from {@link TOUCH_BUTTONS}, the same table that
 * renders the on-screen buttons, so the legend cannot claim a button the player
 * does not have — the touch counterpart of reading keys back through
 * `keyLabelsFor()`.
 *
 * @param {string} [mode] Resolved input mode; `'touch'` swaps the legend.
 * @returns {{ keys: string[], label: string }[]}
 */
function inputRows(mode) {
  if (mode === InputMode.TOUCH) {
    return [
      { keys: ['L stick'], label: 'Move' },
      { keys: ['R stick'], label: 'Aim & Fire' },
      ...TOUCH_BUTTONS.map((button) => ({ keys: ['Button'], label: button.label }))
    ];
  }
  return [
    { keys: ['WASD'], label: 'Move' },
    { keys: ['LMB'], label: 'Fire' },
    { keys: ['RMB'], label: 'Dash' },
    { keys: ['R'], label: 'Reload' },
    { keys: ['T'], label: 'Throw' },
    { keys: ['E'], label: 'Swap' }
  ];
}

/**
 * Formats magazine and reserve ammo as one fast-read HUD label.
 * @param {{ reloadEnabled?: boolean, infiniteReserve?: boolean }} weapon Equipped weapon.
 * @param {number} current Current rounds in the magazine.
 * @param {number} capacity Magazine capacity.
 * @param {number} reserve Spare rounds available for reloads.
 * @returns {{ text: string, html: string }}
 */
function ammoReadout(weapon, current, capacity, reserve) {
  const magazineLabel = `${current}/${capacity}`;
  const showReserve = weapon.reloadEnabled || weapon.infiniteReserve || reserve > 0;
  const reserveLabel = weapon.infiniteReserve ? '∞' : Math.max(0, Math.floor(reserve));
  return {
    text: `${magazineLabel}${showReserve ? ` ${reserveLabel}` : ''}`,
    html:
      `<span class="ammo__magazine">${magazineLabel}</span>` +
      (showReserve ? ` <span class="ammo__reserve">${reserveLabel}</span>` : '')
  };
}

/**
 * Adds the prompt/status word without flattening the styled magazine/reserve spans.
 * @param {string} prefix
 * @param {{ text: string, html: string }} readout
 * @returns {{ text: string, html: string }}
 */
function prefixedAmmoReadout(prefix, readout) {
  return {
    text: `${prefix} ${readout.text}`,
    html: `<span class="ammo__prefix">${prefix}</span> ${readout.html}`
  };
}

export class HUD {
  /**
   * @param {HTMLElement} root Container element, expected to be empty.
   * @param {import('../core/EventBus.js').EventBus} events Shared event bus.
   * @param {import('../config/GameConfig.js').GameConfig} config Game config.
   * @param {object} audioServices Volume-controllable services, narrowed to
   *   just the two the HUD needs rather than the whole game context.
   * @param {import('../audio/AudioManager.js').AudioManager} audioServices.audio SFX volume.
   * @param {import('../audio/MusicDirector.js').MusicDirector} audioServices.music Music volume.
   */
  constructor(root, events, config, { audio, music, inputMode = InputMode.POINTER }) {
    /** @type {HTMLElement} @private */
    this._root = root;
    /** @type {import('../core/EventBus.js').EventBus} @private */
    this._events = events;
    /** @type {import('../config/GameConfig.js').GameConfig} @private */
    this._config = config;
    /**
     * Which legend the Inputs panels show. Read in `_build()`, so it has to be
     * set before the template runs.
     * @type {string}
     * @private
     */
    this._inputMode = inputMode;
    /** @type {import('../audio/AudioManager.js').AudioManager} @private */
    this._audio = audio;
    /** @type {import('../audio/MusicDirector.js').MusicDirector} @private */
    this._music = music;
    /** @type {(() => void)[]} @private */
    this._subscriptions = [];
    /** @type {number} Seconds left on the damage flash. @private */
    this._flashTimer = 0;
    /** @type {number} Seconds left on the wave banner. @private */
    this._bannerTimer = 0;
    /** @type {import('../components/StatusEffects.js').StatusEffects | null} @private */
    this._effects = null;
    /**
     * Live toasts, oldest first, so a FIFO discard is `shift()`.
     * @type {{ id: number, el: HTMLElement, timer: number, removing: boolean }[]}
     * @private
     */
    this._notifications = [];
    /** @type {number} Monotonic id source for toasts. @private */
    this._nextNotificationId = 0;
    /** @type {number} Seconds left before a kill toast may fire again. @private */
    this._killCooldown = 0;
    /** @type {boolean} Whether the center-screen empty-ammo prompt is showing. @private */
    this._emptyPromptShown = false;
    /** @type {number} Seconds left on the empty-prompt shake class. @private */
    this._emptyShakeTimer = 0;
    /**
     * Rounds presented on the last reload frame, so the ammo readout can pop
     * once per newly-seated round. -1 means "not reloading".
     * @type {number}
     * @private
     */
    this._lastReloadRounds = -1;
    /** @type {string | null} Archetype currently rendered on the weapon card. @private */
    this._weaponCardKey = null;

    /**
     * Configured HUD magnification, before the viewport cap.
     * @type {number} @private
     */
    this._defaultConfigScale = coerceHudScale(config.hud.scale);
    /** @type {number} @private */
    this._configScale = this._defaultConfigScale;
    /** @type {Record<string, number>} @private */
    this._defaultComponentScales = buildComponentScales(config.hud.componentScales);
    /** @type {Record<string, number>} @private */
    this._componentScales = { ...this._defaultComponentScales };
    /** @type {string} @private */
    this._defaultKillTimerLabel = config.hud.killTimerLabel ?? 'Kill before time runs out';
    /** @type {string} @private */
    this._killTimerLabel = this._defaultKillTimerLabel;
    /**
     * Effective HUD magnification. Everything laid out by CSS is scaled by
     * `--ui-scale`; the handful of elements this file positions in viewport
     * pixels have to divide by it, because they live inside that scaled
     * coordinate space. Recomputed on resize so a small window never gets a
     * HUD bigger than itself.
     * @type {number} @private
     */
    this._uiScale = this._computeScale();
    /**
     * Latest contextual prompt. The world position is copied out of the event —
     * that payload is reused every frame and must never be retained.
     * @type {{ visible: boolean, prompt: string, label: string, position: THREE.Vector3 }}
     * @private
     */
    this._prompt = { visible: false, prompt: '', label: '', position: new THREE.Vector3() };
    /** @type {THREE.Vector3} Scratch vector for the 3D → screen projection. @private */
    this._projected = new THREE.Vector3();
    /** @type {boolean} Whether the prompt is currently in the DOM flow. @private */
    this._promptShown = false;

    /** @type {() => void} Recomputes the scale cap on resize. @private */
    this._onWindowResize = () => this._applyScale();
    window.addEventListener('resize', this._onWindowResize);

    this._build();
    this._subscribe();
  }

  /**
   * The configured scale, capped so the HUD never overflows the window. The
   * layout is authored for a ~1040x560 CSS-pixel canvas: on a small window the
   * effective scale shrinks until that canvas fits.
   * @returns {number}
   * @private
   */
  _computeScale() {
    const fitWidth = window.innerWidth / 1040;
    const fitHeight = window.innerHeight / 560;
    return Math.max(MIN_UI_SCALE, Math.min(this._configScale, fitWidth, fitHeight));
  }

  /**
   * Publishes the effective scale to CSS: `--ui-scale` zooms the HUD root and
   * `--overlay-zoom` counter-scales the full-screen overlays, which cap at 2×
   * so the menu never outgrows the viewport.
   * @returns {void}
   * @private
   */
  _applyScale() {
    this._uiScale = this._computeScale();
    const root = document.documentElement.style;
    root.setProperty('--ui-scale', String(this._uiScale));
    root.setProperty('--overlay-zoom', String(Math.min(this._uiScale, 2) / this._uiScale));
    for (const id of HUD_COMPONENT_SCALE_IDS) {
      root.setProperty(`--hud-scale-${id}`, String(this._componentScales[id]));
    }
  }

  /** @returns {number} Requested HUD scale before viewport capping. */
  get configuredScale() {
    return this._configScale;
  }

  /** @returns {number} Effective HUD scale after viewport capping. */
  get uiScale() {
    return this._uiScale;
  }

  /**
   * @param {string} id Scale id.
   * @returns {number} Requested scale for that HUD surface.
   */
  getHudScale(id) {
    return id === 'overall' ? this._configScale : this._componentScales[id] ?? 1;
  }

  /** @returns {string} Current top-center countdown title. */
  getKillTimerLabel() {
    return this._killTimerLabel;
  }

  /**
   * @param {string} value Top-center countdown title.
   * @returns {string} Stored label.
   */
  setKillTimerLabel(value) {
    const text = String(value ?? '').trim() || this._defaultKillTimerLabel;
    this._killTimerLabel = text;
    if (this._el?.killTimerTitle) this._el.killTimerTitle.textContent = text;
    return text;
  }

  /**
   * @param {string} id Scale id.
   * @param {number|string} scale Requested HUD scale.
   * @returns {number} Stored scale after clamping.
   */
  setHudScale(id, scale) {
    if (id === 'overall') {
      this._configScale = coerceHudScale(scale, id, this._configScale);
      this._applyScale();
      return this._configScale;
    }

    if (!HUD_COMPONENT_SCALE_IDS.includes(id)) return this.getHudScale(id);
    this._componentScales[id] = coerceHudScale(scale, id, this._componentScales[id]);
    this._applyScale();
    return this._componentScales[id];
  }

  /**
   * @param {{ overall?: number, components?: Record<string, number>, killTimerLabel?: string }} saved Saved debug UI prefs.
   * @returns {void}
   */
  restoreScales(saved) {
    if (!saved) return;
    if (saved.overall !== undefined) {
      this._configScale = coerceHudScale(saved.overall, 'overall', this._configScale);
    }
    if (saved.components && typeof saved.components === 'object') {
      for (const id of HUD_COMPONENT_SCALE_IDS) {
        if (saved.components[id] !== undefined) {
          this._componentScales[id] = coerceHudScale(saved.components[id], id, this._componentScales[id]);
        }
      }
    }
    if (saved.killTimerLabel !== undefined) this.setKillTimerLabel(saved.killTimerLabel);
    this._applyScale();
  }

  /**
   * @returns {{ overall: number, components: Record<string, number>, killTimerLabel: string }} Snapshot suitable for debug prefs.
   */
  snapshotScales() {
    return {
      overall: this._configScale,
      components: { ...this._componentScales },
      killTimerLabel: this._killTimerLabel
    };
  }

  /**
   * @returns {{ overall: number, components: Record<string, number>, killTimerLabel: string }} Defaults restored snapshot.
   */
  resetScales() {
    this._configScale = this._defaultConfigScale;
    this._componentScales = { ...this._defaultComponentScales };
    this.setKillTimerLabel(this._defaultKillTimerLabel);
    this._applyScale();
    return this.snapshotScales();
  }

  /** @returns {void} @private */
  _build() {
    this._root.innerHTML = `
      <div class="damage-flash" data-hud="flash"></div>
      <div class="crosshair" data-hud="crosshair"></div>
      <div class="cursor-dot" data-hud="cursorDot"></div>

      <div class="banner" data-hud="banner">
        <span class="banner__label" data-hud="bannerLabel"></span>
      </div>

      <div class="interact-prompt" data-hud="interactPrompt">
        <kbd class="interact-prompt__key" data-hud="interactKey">E</kbd>
        <span class="interact-prompt__label" data-hud="interactLabel"></span>
      </div>

      <div class="empty-prompt" data-hud="emptyPrompt">
        <kbd class="empty-prompt__key" data-hud="emptyPromptKey">R</kbd>
        <span class="empty-prompt__label" data-hud="emptyPromptLabel"></span>
      </div>

      <div class="hud-corner hud-corner--tl">
        <div class="objective-panel hud-panel" data-hud="objectivePanel">
          <div class="objective-panel__label">Objective</div>
          <div class="objective-panel__desc">Reach the next portal</div>
          <div class="objective-panel__compass">
            <div class="compass-dial">
              <div class="compass-dial__arrow" data-hud="compassArrow">${COMPASS_ARROW_ICON}</div>
            </div>
            <div class="objective-panel__distance">
              <span data-hud="objectiveDistance">--</span><span class="unit">m</span>
            </div>
          </div>
        </div>
      </div>

      <div class="pulse-bar hud-panel" data-hud="killTimer">
        <div class="pulse-bar__title" data-hud="killTimerTitle">${this._killTimerLabel}</div>
        <div class="pulse-bar__row">
          <span class="pulse-bar__skull">${SKULL_ICON}</span>
          <div class="pulse-bar__segments" data-hud="killTimerSegments">${pulseSegmentsHtml()}</div>
          <span class="pulse-bar__value" data-hud="killTimerValue">0.0s</span>
        </div>
        <div class="pulse-bar__warning">Time is running out</div>
      </div>

      <div class="hud-corner hud-corner--bl">
        <pre class="weapon-debug" data-hud="weaponDebug"></pre>
        <div class="immortal-counter" data-hud="immortalCounter"></div>
        <div class="shield-bar" data-hud="shieldBarRow">
          <span class="shield-bar__icon">${SHIELD_ICON}</span>
          <div class="shield-bar__track">
            <div class="shield-bar__fill" data-hud="shieldFill"></div>
          </div>
        </div>
        <div class="buffs" data-hud="buffs"></div>
      </div>

      <div class="hud-corner hud-corner--tr">
        <div class="stats-panel hud-panel">
          <div class="hud-stat"><span class="hud-stat__icon">${FLOOR_ICON}</span>Floor <span class="hud-stat__value" data-hud="floor">1</span></div>
          <div class="hud-stat"><span class="hud-stat__icon">${SCORE_ICON}</span>Score <span class="hud-stat__value" data-hud="score">0</span></div>
        </div>
      </div>

      <div class="hud-corner hud-corner--bc">
        <div class="notification-stack" data-hud="notifications"></div>
      </div>

      <div class="hud-corner hud-corner--br">
        <div class="input-card hud-panel" data-hud="inputCard">
          <div class="input-card__title">INPUTS</div>
          ${inputRows(this._inputMode)
            .map(
              (row) => `
                <div class="input-card__row">
                  <span>${row.keys.join(' / ')}</span>
                  <span>${row.label}</span>
                </div>
              `
            )
            .join('')}
        </div>
        <div class="weapon-card hud-panel">
          <img class="weapon-card__art" data-hud="weaponArt" alt="" aria-hidden="true" />
          <div class="weapon-card__ammo">
            <span class="weapon-card__name" data-hud="weapon"></span>
            <span class="ammo" data-hud="ammo"></span>
          </div>
          <div class="weapon-card__pips">${BULLET_ICON}${BULLET_ICON}${BULLET_ICON}</div>
        </div>
      </div>

      <div class="overlay" data-hud="overlay">
        <div class="menu-panel">
          <h1 class="menu-panel__title" data-hud="overlayTitle"></h1>
          <div class="menu-panel__divider"><span class="menu-panel__divider-dot"></span></div>
          <p class="menu-panel__subtitle" data-hud="overlaySubtitle"></p>
          <p class="overlay__hint" data-hud="overlayHint"></p>

          <div class="menu-panel__body" data-hud="menuBody">
            <nav class="menu-panel__nav" data-hud="menuNav"></nav>

            <div class="menu-panel__settings" data-hud="settings">
              <section class="menu-section">
                <h2 class="menu-section__title">Inputs</h2>
                <div class="controls-grid" data-hud="controlsGrid"></div>
              </section>

              <section class="menu-section">
                <h2 class="menu-section__title">Audio</h2>
                <div class="volume-list">
                  <label class="volume-row">
                    <span>Music</span>
                    <input type="range" min="0" max="100" step="1" data-hud="musicSlider" />
                  </label>
                  <label class="volume-row">
                    <span>SFX</span>
                    <input type="range" min="0" max="100" step="1" data-hud="sfxSlider" />
                  </label>
                </div>
              </section>

            </div>
          </div>

          <p class="menu-panel__footer" data-hud="menuFooter"></p>
        </div>
      </div>

      <div class="confirm-modal" data-hud="confirmModal">
        <div class="confirm-modal__box">
          <p class="confirm-modal__text">Quit to main menu?</p>
          <p class="confirm-modal__subtext">The current run will be lost.</p>
          <div class="confirm-modal__actions">
            <button type="button" class="confirm-modal__btn confirm-modal__btn--yes" data-hud="confirmYes">
              Quit
            </button>
            <button type="button" class="confirm-modal__btn" data-hud="confirmNo">Cancel</button>
          </div>
        </div>
      </div>
    `;

    /** @type {Record<string, HTMLElement>} @private */
    this._el = {};
    for (const node of this._root.querySelectorAll('[data-hud]')) {
      this._el[/** @type {HTMLElement} */ (node).dataset.hud] = /** @type {HTMLElement} */ (node);
    }
    /** @type {HTMLElement[]} Pulse-bar segment nodes, cached for {@link _setKillTimer}. @private */
    this._pulseSegments = Array.from(this._el.killTimerSegments.children);

    this._applyScale();

    // A finger has no key to press — hide the "E" badge for good and leave the
    // prompt's own label (the pickup's name) as the only thing on screen.
    // Fixed for the run: `_inputMode` is decided once at boot, never live.
    if (this._inputMode === InputMode.TOUCH) this._el.interactKey.style.display = 'none';

    this._el.weaponDebug.style.display = this._config.debug.showWeapon ? '' : 'none';
    // The card's markup carries the starting weapon's name but no portrait;
    // this is what puts one there before the first WEAPON_CHANGED lands.
    this._setWeaponCard(WEAPONS[DEFAULT_WEAPON], DEFAULT_WEAPON);
    this._buildVolumeSliders();
    this._buildConfirmModal();
  }

  /**
   * Wires the two volume sliders once; only their visibility changes per state.
   * @returns {void}
   * @private
   */
  _buildVolumeSliders() {
    const music = /** @type {HTMLInputElement} */ (this._el.musicSlider);
    const sfx = /** @type {HTMLInputElement} */ (this._el.sfxSlider);

    music.value = String(Math.round(this._music.getVolume() * 100));
    sfx.value = String(Math.round(this._audio.getVolume() * 100));

    // `change`, not `input`, for the report: dragging a slider fires `input`
    // per pixel, and only the value the player settled on is worth an event.
    music.addEventListener('input', () => this._music.setVolume(Number(music.value) / 100));
    music.addEventListener('change', () =>
      this._events.emit(Events.SETTINGS_CHANGED, { setting: 'music_volume', value: Number(music.value) })
    );
    sfx.addEventListener('input', () => this._audio.setVolume(Number(sfx.value) / 100));
    sfx.addEventListener('change', () =>
      this._events.emit(Events.SETTINGS_CHANGED, { setting: 'sfx_volume', value: Number(sfx.value) })
    );
  }

  /**
   * Wires the quit-confirmation popup's buttons. `PausedState` owns the
   * decision of what "confirmed" means; the HUD only reports the click.
   * @returns {void}
   * @private
   */
  _buildConfirmModal() {
    this._el.confirmYes.addEventListener('click', () =>
      this._events.emit(Events.CONFIRM_QUIT_RESPONSE, { confirmed: true })
    );
    this._el.confirmNo.addEventListener('click', () =>
      this._events.emit(Events.CONFIRM_QUIT_RESPONSE, { confirmed: false })
    );
  }

  /** @returns {void} @private */
  _subscribe() {
    this._subscriptions.push(
      this._events.on(Events.STATE_CHANGED, ({ state }) => this._onStateChanged(state)),
      this._events.on(Events.PLAYER_KILLTIMER_CHANGED, ({ current, max }) =>
        this._setKillTimer(current, max)
      ),
      this._events.on(Events.SCORE_CHANGED, (payload) => this._onScore(payload)),
      // `DAMAGE_APPLIED`, not `DAMAGE`: the flash must only answer to a hit that
      // survived shields, i-frames and god mode (issue #74).
      this._events.on(Events.DAMAGE_APPLIED, (payload) => this._onDamageApplied(payload)),
      this._events.on(Events.WEAPON_CHANGED, ({ weapon }) => {
        // A null weapon is the "just threw it away" signal (issue #15).
        this._setWeaponCard(weapon);
        this._showBanner(weapon ? weapon.name : 'Unarmed');
        if (weapon) this._addNotification(weapon.name, 'weapon');
        // A fresh weapon starts its own ammo story; drop any leftover prompt.
        this._hideEmptyPrompt();
      }),
      // A magazine ran dry, or the trigger was pulled on an empty one. The
      // first shows the center-screen prompt; the second also jolts it so a
      // dry-fire reads as a rejected input rather than a dead click.
      this._events.on(Events.WEAPON_EMPTY, ({ weapon }) => this._showEmptyPrompt(weapon)),
      this._events.on(Events.WEAPON_EMPTY_TRIGGER, ({ weapon }) => {
        this._showEmptyPrompt(weapon);
        this._shakeEmptyPrompt();
      }),
      // Reloading or swapping away resolves the empty state; hide either way.
      this._events.on(Events.RELOAD_STARTED, () => this._hideEmptyPrompt()),
      this._events.on(Events.RELOAD_COMPLETED, () => this._hideEmptyPrompt()),
      // Single-round interrupt commits seated ammo (issue #93); if that leaves
      // the magazine empty, the dry-fire path will re-show the prompt.
      this._events.on(Events.RELOAD_INTERRUPTED, () => this._hideEmptyPrompt()),
      this._events.on(Events.STATUS_CHANGED, ({ effects }) => {
        this._effects = effects;
      }),
      this._events.on(Events.INTERACTION_PROMPT, (payload) => this._onPrompt(payload)),
      this._events.on(Events.WAVE_STARTED, ({ wave }) => this._showBanner(`Level ${wave}`)),
      this._events.on(Events.WAVE_CLEARED, () => this._showBanner('Clear')),
      this._events.on(Events.PICKUP_COLLECTED, ({ definition }) => {
        this._showBanner(definition.name);
        // Weapon pickups already toast via WEAPON_CHANGED (type `weapon`); a
        // second identical toast here would double-stack the same name.
        if (definition.kind === 'weapon') return;
        this._addNotification(definition.name, 'pickup');
      }),
      this._events.on(Events.DIED, ({ entity }) => this._onKill(entity)),
      this._events.on(Events.NOTIFY_ADD, ({ text, type, duration }) =>
        this._addNotification(text, type, duration)
      ),
      this._events.on(Events.LEVEL_READY, ({ levelIndex }) => {
        if (this._el.floor) this._el.floor.textContent = String(levelIndex ?? 1);
        this._showBanner(getRandomLevelName());
      }),
      this._events.on(Events.LEVEL_COMPLETE, () => this._showBanner('Next floor')),
      this._events.on(Events.CONFIRM_QUIT_PROMPT, ({ visible }) => {
        // Base CSS hides it (`display: none`); an empty string would just
        // fall back to that same rule, so the visible case needs a value.
        this._el.confirmModal.style.display = visible ? 'flex' : 'none';
      })
    );
  }

  /**
   * @param {string} state New state name.
   * @returns {void}
   * @private
   */
  _onStateChanged(state) {
    const overlay = OVERLAYS[state];
    const visible = Boolean(overlay);
    const isMenuLayout = overlay?.layout === 'menu';

    this._el.overlay.classList.toggle('overlay--visible', visible);
    // The gameplay chrome (stats, kill timer, weapon card, toasts) reads as
    // noise blurred behind a full-screen overlay — hide it outright.
    this._root.classList.toggle('hud--overlay-open', visible);
    this._el.crosshair.style.display = state === GameStates.PLAYING ? '' : 'none';
    // Base CSS hides these (`display: none`); clearing the inline style would
    // just fall back to that same rule, so the visible case needs a value.
    this._el.menuBody.style.display = isMenuLayout ? 'flex' : 'none';
    // The OS cursor stays hidden everywhere (`cursor: none` on the canvas, so
    // the gameplay crosshair is never doubled by a system arrow); this dot is
    // the only way to see the pointer while reaching for the nav list or a
    // slider.
    this._el.cursorDot.style.display = isMenuLayout ? 'block' : 'none';
    // Only FRENZY (menu) and PAUSED share the breathing title; GAME_OVER's
    // "simple" layout reuses the same <h1> and stays static.
    this._el.overlayTitle.classList.toggle('menu-panel__title--breathe', isMenuLayout);

    // Only the playing state ticks the world, so nothing would ever tell the
    // prompt to hide itself once the simulation stops.
    if (state !== GameStates.PLAYING) this._hidePrompt();

    if (state === GameStates.LOADING) {
      // A fresh run: reset every readout the events would otherwise not resend.
      const { killTimerMax } = getPlayerTuning();
      this._setKillTimer(killTimerMax, killTimerMax);
      // The run always starts on the default sidearm, so that is what the
      // card must show; the per-frame weapon pass fills the magazine readout.
      this._setWeaponCard(WEAPONS[DEFAULT_WEAPON], DEFAULT_WEAPON);
      this._el.ammo.textContent = '';
      this._el.ammo.classList.remove('ammo--empty', 'ammo--reloading', 'ammo--tick');
      this._lastReloadRounds = -1;
      this._hideEmptyPrompt();
      this._el.shieldFill.style.transform = 'scaleX(0)';
      this._el.shieldBarRow.style.display = 'none';
      this._el.immortalCounter.textContent = '';
      this._el.buffs.innerHTML = '';
      this._effects = null;
      this._killCooldown = 0;
      // Drop any leftover toasts from the previous run / floor so they cannot
      // linger over a full-health spawn or bleed into the next level.
      for (const n of this._notifications) n.el.remove();
      this._notifications.length = 0;
    }

    if (!visible) return;
    this._el.overlayTitle.textContent = overlay.title;
    this._el.overlaySubtitle.textContent = overlay.subtitle ?? '';
    this._el.overlayHint.textContent = overlay.hint ?? '';
    // Static internal copy only (never user input) — `<kbd>` badges match the
    // Input panel's key styling.
    this._el.menuFooter.innerHTML = overlay.footer ?? '';

    if (!isMenuLayout) return;
    this._renderNav(overlay.nav ?? []);
    this._renderControlsGrid();
  }

  /**
   * Builds the nav list (Play / Resume·Settings·Main Menu). Rebuilt on every
   * state change rather than diffed — at most three rows, and it only happens
   * on a state transition, never per frame.
   *
   * @param {{ action: string, label: string, icon: string, primary?: boolean }[]} items
   * @returns {void}
   * @private
   */
  _renderNav(items) {
    this._el.menuNav.innerHTML = items
      .map(
        (item) => `
          <button type="button" class="menu-nav__item${item.primary ? ' menu-nav__item--primary' : ''}"
                  data-nav-action="${item.action}">
            <span class="menu-nav__icon">${NAV_ICONS[item.icon] ?? ''}</span>
            <span class="menu-nav__label">${item.label}</span>
          </button>
        `
      )
      .join('');

    for (const button of this._el.menuNav.querySelectorAll('[data-nav-action]')) {
      button.addEventListener('click', () =>
        this._events.emit(Events.MENU_NAV_ACTION, { action: button.dataset.navAction })
      );
    }
  }

  /**
   * Builds the Inputs grid from {@link inputRows}.
   * @returns {void}
   * @private
   */
  _renderControlsGrid() {
    this._el.controlsGrid.innerHTML = inputRows(this._inputMode)
      .map(
        (row) => `
          <div class="controls-row">
            <span class="controls-row__keys">${row.keys.map((k) => `<kbd>${k}</kbd>`).join('')}</span>
            <span class="controls-row__label">${row.label}</span>
          </div>
        `
      )
      .join('');
  }

  /**
   * Draws the kill countdown: the player's only life bar.
   *
   * @param {number} current Seconds left before the countdown kills the player.
   * @param {number} max Countdown ceiling in seconds.
   * @returns {void}
   * @private
   */
  _setKillTimer(current, max) {
    const seconds = Math.max(0, current);
    const ratio = max > 0 ? seconds / max : 0;
    const lit = Math.round(ratio * PULSE_SEGMENTS);
    for (let i = 0; i < this._pulseSegments.length; i++) {
      this._pulseSegments[i].classList.toggle('pulse-bar__segment--dim', i >= lit);
    }
    this._el.killTimerValue.textContent = `${seconds.toFixed(1)}s`;
    // "Time is running out" lives right under the countdown bar itself (as
    // `.pulse-bar__warning`) rather than a corner toast, so it stays visible
    // for the whole low-timer window instead of firing once and scrolling
    // away with the rest of the notification stack.
    this._el.killTimer.classList.toggle(
      'pulse-bar--low',
      ratio <= this._config.hud.lowKillTimerThreshold && seconds > 0
    );
  }

  /**
   *
   * Draws the equipped-weapon card: name plus the pack portrait of the gun.
   *
   * The portrait is hidden rather than swapped for a blank when there is no art
   * — an `<img>` with an empty `src` re-requests the document in some browsers,
   * and a missing weapon should collapse the slot anyway.
   *
   * @param {{ key?: string, name?: string } | null} weapon Equipped weapon, or
   *   null once the player has thrown it away (issue #15).
   * @param {string} [key] Archetype key, for callers holding an archetype from
   *   the table rather than a live `Weapon` component.
   * @returns {void}
   * @private
   */
  _setWeaponCard(weapon, key = weapon?.key) {
    this._weaponCardKey = weapon ? key ?? null : null;
    this._el.weapon.textContent = weapon ? weapon.name : '—';

    const art = /** @type {HTMLImageElement} */ (this._el.weaponArt);
    const sprite = weapon ? weaponSprite(key) : null;
    if (sprite) art.src = sprite;
    art.style.display = sprite ? '' : 'none';
  }

  /**
   * @param {{ score: number }} payload Score event.
   * @returns {void}
   * @private
   */
  _onScore({ score }) {
    this._el.score.textContent = String(score);
  }

  /**
   * Screen flash for a hit the player really took. `DAMAGE_APPLIED` reports every
   * confirmed hit in the game, so the player filter lives here — presentation is
   * the HUD's job, and the alternative was a second event carrying the same
   * guarantee. A hit landing while the flash is still up simply refills the timer.
   *
   * @param {{ target?: import('../ecs/Entity.js').Entity }} payload Confirmed damage.
   * @returns {void}
   * @private
   */
  _onDamageApplied({ target } = {}) {
    if (!target?.alive || !target.has?.(PlayerControlled)) return;

    const { flashColor, flashDuration } = getPlayerTuning().damageFeedback;
    this._flashTimer = flashDuration;
    // The CSS fade-out has to follow the configured duration, or a long flash
    // would still vanish in the stylesheet's 220ms.
    this._el.flash.style.setProperty('--damage-flash-fade', `${this._flashTimer * 1000}ms`);
    this._el.flash.style.setProperty('--damage-flash-color', rgbaFromHex(flashColor, FLASH_ALPHA));
    this._el.flash.classList.add('damage-flash--on');
  }

  /**
   * @param {{ visible: boolean, prompt: string, label: string,
   *           position: THREE.Vector3 | null }} payload Prompt state. The
   *   payload object is reused by the emitting system, so everything kept here
   *   is copied out of it.
   * @returns {void}
   * @private
   */
  _onPrompt({ visible, prompt, label, position }) {
    this._prompt.visible = visible && Boolean(position);
    if (!this._prompt.visible) {
      this._hidePrompt();
      return;
    }

    this._prompt.position.copy(position);

    if (this._prompt.prompt !== prompt) {
      this._prompt.prompt = prompt;
      this._el.interactKey.textContent = prompt;
    }
    if (this._prompt.label !== label) {
      this._prompt.label = label;
      this._el.interactLabel.textContent = label;
    }
  }

  /** @returns {void} @private */
  _hidePrompt() {
    this._prompt.visible = false;
    if (!this._promptShown) return;
    this._promptShown = false;
    this._el.interactPrompt.style.display = 'none';
  }

  /**
   * Projects the prompt onto the viewport. Done here rather than in the system
   * that raised it because the camera is only final once the simulation has
   * finished the frame — projecting any earlier trails the world by a frame.
   *
   * @param {import('../core/Context.js').GameContext} ctx Shared game context.
   * @returns {void}
   * @private
   */
  _renderPrompt(ctx) {
    if (!this._prompt.visible) return;

    const { position } = this._prompt;
    this._projected
      .set(position.x, position.y + this._config.interaction.promptHeight, position.z)
      .project(ctx.cameraController.camera);

    // Behind the camera: `project` mirrors those points into the viewport.
    if (this._projected.z > 1) {
      this._hidePrompt();
      return;
    }

    const { width, height } = ctx.renderer.size;
    // The HUD root is zoomed by --ui-scale, so viewport pixels divide by it.
    const x = ((this._projected.x * 0.5 + 0.5) * width) / this._uiScale;
    const y = ((-this._projected.y * 0.5 + 0.5) * height) / this._uiScale;

    if (!this._promptShown) {
      this._promptShown = true;
      this._el.interactPrompt.style.display = 'flex';
    }
    this._el.interactPrompt.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
  }

  /**
   * @param {string} text Banner copy.
   * @returns {void}
   * @private
   */
  _showBanner(text) {
    this._el.bannerLabel.textContent = text;
    this._el.banner.classList.remove('banner--on');
    // Force a reflow so re-triggering the animation actually restarts it.
    void this._el.banner.offsetWidth;
    this._el.banner.classList.add('banner--on');
    this._bannerTimer = this._config.hud.bannerDuration;
  }

  /**
   * Shows the center-screen empty-ammo prompt. Auto-reloading weapons with
   * reserve skip the prompt entirely; manual reloads still get "PRESS R TO
   * RELOAD", and weapons with nothing left get the throw prompt.
   *
   * On touch there is no key to press — the on-screen Reload/Throw button
   * itself glows instead (`TouchControls._setEmptyAction`, driven by the same
   * {@link emptyWeaponAction} decision) — so the badge is hidden and the copy
   * drops the "PRESS X TO" lead-in.
   *
   * @param {import('../components/Weapon.js').Weapon} weapon The empty weapon.
   * @returns {void}
   * @private
   */
  _showEmptyPrompt(weapon) {
    const action = emptyWeaponAction(weapon);
    if (!action) return;
    const touch = this._inputMode === InputMode.TOUCH;

    if (action === 'reload') {
      this._el.emptyPromptKey.textContent = keyLabelsFor(Action.RELOAD)[0] ?? 'R';
      this._el.emptyPromptLabel.textContent = touch ? 'RELOAD' : 'PRESS R TO RELOAD';
      this._el.emptyPromptKey.style.display = touch ? 'none' : '';
    } else {
      this._el.emptyPromptKey.textContent = keyLabelsFor(Action.THROW)[0] ?? 'T';
      this._el.emptyPromptLabel.textContent = touch ? 'WEAPON EMPTY! THROW IT' : 'WEAPON EMPTY! PRESS T TO THROW IT';
      this._el.emptyPromptKey.style.display = touch || weapon.throwable === false ? 'none' : '';
    }
    this._el.emptyPrompt.classList.add('empty-prompt--visible');
    this._emptyPromptShown = true;
  }

  /**
   * Hides the empty-ammo prompt and clears any in-flight shake.
   * @returns {void}
   * @private
   */
  _hideEmptyPrompt() {
    if (!this._emptyPromptShown && this._emptyShakeTimer <= 0) return;
    this._el.emptyPrompt.classList.remove('empty-prompt--visible', 'empty-prompt--shake');
    this._emptyPromptShown = false;
    this._emptyShakeTimer = 0;
  }

  /**
   * Jolts the prompt to acknowledge a dry-fire. No-op while the prompt is
   * hidden, so a shake never flashes an otherwise-absent element.
   * @returns {void}
   * @private
   */
  _shakeEmptyPrompt() {
    if (!this._emptyPromptShown) return;
    this._el.emptyPrompt.classList.remove('empty-prompt--shake');
    // Force a reflow so re-triggering the animation actually restarts it.
    void this._el.emptyPrompt.offsetWidth;
    this._el.emptyPrompt.classList.add('empty-prompt--shake');
    this._emptyShakeTimer = 0.3;
  }

  /**
   * Turns an enemy death into a "Kill: <name>" toast. Fires for enemies only
   * (the player carries no archetype) and throttled by a short cooldown so a
   * burst of kills does not flood the stack.
   *
   * @param {import('../ecs/Entity.js').Entity} entity The entity that died.
   * @returns {void}
   * @private
   */
  _onKill(entity) {
    const archetype = entity?.archetypeKey ? ENEMIES[entity.archetypeKey] : null;
    if (!archetype) return;
    if (this._killCooldown > 0) return;
    this._killCooldown = KILL_COOLDOWN;
    this._addNotification(`Kill: ${archetype.name}`, 'kill');
  }

  /**
   * Pushes a toast onto the corner stack. The entry animation runs from CSS the
   * moment the node is inserted; {@link _tickNotification} owns the lifetime and
   * exit. When the stack is full the oldest toast is dropped immediately (FIFO),
   * so the DOM never holds more than `maxStack` nodes.
   *
   * @param {string} text Copy to show.
   * @param {string} [type] Style/lifetime bucket (weapon|pickup|kill|warning|…).
   * @param {number} [duration] Explicit lifetime in seconds; overrides the type.
   * @returns {number} The new toast's id, usable with {@link _removeNotification}.
   * @private
   */
  _addNotification(text, type = 'default', duration) {
    const cfg = this._config.hud.notifications;
    const life = duration ?? cfg.durations[type] ?? cfg.durations.default ?? cfg.baseDuration;

    // FIFO discard: hard-remove the oldest until we are within the cap.
    while (this._notifications.length >= cfg.maxStack) {
      const oldest = this._notifications.shift();
      oldest.el.remove();
    }

    const id = this._nextNotificationId++;
    const el = document.createElement('div');
    el.className = `notification notification--${type}`;
    el.textContent = text;
    // Newest on top (FR-04): column flex first-child is the visual top.
    this._el.notifications.prepend(el);

    this._notifications.push({ id, el, timer: life, removing: false });
    return id;
  }

  /**
   * Begins the exit animation for a toast ahead of its natural expiry; the tick
   * removes the node once the animation window elapses. No-op if it is unknown
   * or already leaving.
   *
   * @param {number} id Id returned by {@link _addNotification}.
   * @returns {void}
   * @private
   */
  _removeNotification(id) {
    const n = this._notifications.find((entry) => entry.id === id);
    if (!n || n.removing) return;
    n.removing = true;
    n.timer = NOTIFICATION_EXIT;
    n.el.classList.add('notification--out');
  }

  /**
   * Advances every toast's timer. A live toast that expires flips to its exit
   * animation; a leaving toast whose window has elapsed is spliced out and its
   * node removed, so no orphan elements survive. Bounded by `maxStack`, this is
   * O(1) per toast per frame.
   *
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   * @private
   */
  _tickNotification(dt) {
    if (this._killCooldown > 0) this._killCooldown -= dt;

    const list = this._notifications;
    for (let i = list.length - 1; i >= 0; i--) {
      const n = list[i];
      n.timer -= dt;
      if (n.timer > 0) continue;

      if (n.removing) {
        n.el.remove();
        list.splice(i, 1);
      } else {
        n.removing = true;
        n.timer = NOTIFICATION_EXIT;
        n.el.classList.add('notification--out');
      }
    }
  }

  /**
   * Per-frame refresh for things that are not event-driven.
   *
   * @param {number} dt Delta time in seconds.
   * @param {import('../core/Context.js').GameContext} ctx Shared game context.
   * @returns {void}
   */
  update(dt, ctx) {
    const { pointer } = ctx.input;
    // .crosshair/.cursor-dot carry their own `zoom: var(--hud-scale-crosshair)`
    // (for their visual size) on the very element this `transform` positions,
    // and CSS zoom rescales a same-element transform's translate too — so that
    // factor has to come back out here as well, not just --ui-scale's.
    const scale = this._uiScale * this._componentScales.crosshair;
    const px = pointer.x / scale;
    const py = pointer.y / scale;
    this._el.crosshair.style.transform = `translate(${px}px, ${py}px)`;
    this._el.cursorDot.style.transform = `translate(${px}px, ${py}px)`;

    if (this._flashTimer > 0) {
      this._flashTimer -= dt;
      if (this._flashTimer <= 0) this._el.flash.classList.remove('damage-flash--on');
    }

    if (this._bannerTimer > 0) {
      this._bannerTimer -= dt;
      if (this._bannerTimer <= 0) this._el.banner.classList.remove('banner--on');
    }

    if (this._emptyShakeTimer > 0) {
      this._emptyShakeTimer -= dt;
      if (this._emptyShakeTimer <= 0) this._el.emptyPrompt.classList.remove('empty-prompt--shake');
    }

    this._renderPrompt(ctx);
    this._tickNotification(dt);
    this._renderStatus();
    this._renderWeapon(ctx);
    this._renderObjective(ctx);
  }

  /**
   * Draws the objective compass: bearing and distance to the level exit.
   * Hidden whenever there is no arena/exit or the player is gone.
   *
   * @param {import('../core/Context.js').GameContext} ctx Shared game context.
   * @returns {void}
   * @private
   */
  _renderObjective(ctx) {
    const exit = ctx.arena?.exit;
    const transform = ctx.player?.alive ? ctx.player.get(Transform) : null;
    const visible = Boolean(exit && transform);
    this._el.objectivePanel.style.visibility = visible ? '' : 'hidden';
    if (!visible) return;

    const dx = exit.x - transform.position.x;
    const dz = exit.z - transform.position.z;
    // Camera has a fixed orientation (no yaw), so world-to-screen bearing is a
    // constant formula: screen-up is world -Z, screen-right is world +X.
    const bearingDeg = Math.atan2(dx, -dz) * (180 / Math.PI);
    this._el.compassArrow.style.transform = `rotate(${bearingDeg}deg)`;

    const meters = String(Math.round(Math.hypot(dx, dz)));
    if (this._el.objectiveDistance.textContent !== meters) {
      this._el.objectiveDistance.textContent = meters;
    }
  }

  /**
   * Draws the ammo readout straight off the player's weapon. Reading the
   * component rather than mirroring it into HUD state means the counter can
   * never drift from the weapon that owns it.
   *
   * @param {import('../core/Context.js').GameContext} ctx Shared game context.
   * @returns {void}
   * @private
   */
  _renderWeapon(ctx) {
    const weapon = ctx.player?.alive ? ctx.player.get(Weapon) : null;
    if (!weapon) {
      if (this._weaponCardKey !== null) this._setWeaponCard(null);
      if (this._el.ammo.textContent !== '') this._el.ammo.textContent = '';
      if (this._config.debug.showWeapon) this._el.weaponDebug.textContent = '';
      return;
    }
    if (weapon.key !== this._weaponCardKey) this._setWeaponCard(weapon);

    let label;
    const reloading = weapon.isReloading();
    const unlimitedAmmo = statusRemaining(this._effects, TimedStatus.UNLIMITED_AMMO) > 0;
    if (reloading) {
      // Discrete, integer-stepped magazine that fills bala-by-bala — never a
      // percentage (issue #78). The reserve count follows the same preview so
      // the paired numbers move together while a reload is being presented.
      const preview = weapon.getReloadPreviewAmmo();
      label = prefixedAmmoReadout(
        'RELOAD',
        ammoReadout(weapon, preview.current, preview.capacity, preview.reserve)
      );
      this._pulseReloadTick(preview.roundsLoaded);
    } else {
      this._lastReloadRounds = -1;
      if (weapon.infiniteAmmo || unlimitedAmmo) {
        label = { text: '∞', html: '∞' };
      } else if (weapon.getCurrentAmmo() === 0) {
        const canReload = weapon.reloadEnabled && (weapon.infiniteReserve || weapon.reserveAmmo > 0);
        const readout = ammoReadout(
          weapon,
          weapon.getCurrentAmmo(),
          weapon.getMagazineCapacity(),
          weapon.getReserveAmmo()
        );
        label = prefixedAmmoReadout(canReload ? 'RELOAD' : 'EMPTY', readout);
      } else {
        label = ammoReadout(
          weapon,
          weapon.getCurrentAmmo(),
          weapon.getMagazineCapacity(),
          weapon.getReserveAmmo()
        );
      }
    }

    if (this._el.ammo.textContent !== label.text) this._el.ammo.innerHTML = label.html;
    const empty = !reloading && !weapon.infiniteAmmo && !unlimitedAmmo && weapon.getCurrentAmmo() === 0;
    this._el.ammo.classList.toggle('ammo--reloading', reloading);
    this._el.ammo.classList.toggle('ammo--empty', empty);

    // Safety net for the "no longer empty" case (e.g. an ammo pickup that
    // refills the mag, or a reload in progress) with no event of its own.
    if (this._emptyPromptShown && !empty) this._hideEmptyPrompt();

    if (this._config.debug.showWeapon) this._el.weaponDebug.textContent = describeWeapon(weapon);
  }

  /**
   * Pops the ammo readout once each time a fresh round is presented, so the
   * bala-by-bala fill reads as motion and not just a changing number. Restarting
   * the CSS animation needs a forced reflow between removing and re-adding the
   * class, exactly like {@link _showBanner}.
   *
   * @param {number} roundsLoaded Rounds presented so far this reload.
   * @returns {void}
   * @private
   */
  _pulseReloadTick(roundsLoaded) {
    if (roundsLoaded === this._lastReloadRounds) return;
    this._lastReloadRounds = roundsLoaded;
    if (roundsLoaded <= 0) return;

    const el = this._el.ammo;
    el.classList.remove('ammo--tick');
    void el.offsetWidth;
    el.classList.add('ammo--tick');
  }

  /**
   * Draws the shield bar and the live buff timers. Read straight off the
   * component rather than mirrored into HUD state, so it can never drift.
   *
   * @returns {void}
   * @private
   */
  _renderStatus() {
    const effects = this._effects;
    if (!effects) {
      if (this._el.immortalCounter.textContent !== '') this._el.immortalCounter.textContent = '';
      this._el.shieldBarRow.style.display = 'none';
      return;
    }

    // No shield up: the icon/track would just be an empty, meaningless bar.
    this._el.shieldBarRow.style.display = effects.shield > 0 ? '' : 'none';
    const shieldRatio = Math.min(1, effects.shield / this._config.hud.shieldScale);
    this._el.shieldFill.style.transform = `scaleX(${shieldRatio})`;

    const immortalRemaining = statusRemaining(effects, TimedStatus.IMMORTAL);
    const immortalLabel = immortalRemaining > 0 ? `Immortal: ${immortalRemaining.toFixed(1)}s` : '';
    if (this._el.immortalCounter.textContent !== immortalLabel) {
      this._el.immortalCounter.textContent = immortalLabel;
    }

    const unlimitedAmmo = statusRemaining(effects, TimedStatus.UNLIMITED_AMMO);
    if (effects.active.size === 0 && unlimitedAmmo <= 0) {
      if (this._el.buffs.childElementCount > 0) this._el.buffs.innerHTML = '';
      return;
    }

    // Buff chips are few (three stats at most), so rebuilding them is cheaper
    // than diffing them.
    let html = '';
    for (const [stat, effect] of effects.active) {
      const ratio = Math.max(0, effect.remaining / effect.duration);
      html +=
        `<span class="buff buff--${stat}">${stat}` +
        `<i style="transform:scaleX(${ratio.toFixed(3)})"></i></span>`;
    }
    if (unlimitedAmmo > 0) {
      const ammoEffect = effects.timed.get(TimedStatus.UNLIMITED_AMMO);
      const ratio = Math.max(0, unlimitedAmmo / ammoEffect.duration);
      html +=
        `<span class="buff buff--ammo">ammo` +
        `<i style="transform:scaleX(${ratio.toFixed(3)})"></i></span>`;
    }
    this._el.buffs.innerHTML = html;
  }

  /**
   * @returns {void}
   */
  dispose() {
    window.removeEventListener('resize', this._onWindowResize);
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;
    // Clearing the array too drops the references to the just-orphaned nodes.
    this._notifications.length = 0;
    this._root.innerHTML = '';
  }
}
